import { reportClientErrorOnce } from '@/lib/clientErrorLog';

/**
 * Wake lock dello schermo durante una chiamata.
 *
 * Le call passano dal browser: per Android non è una "telefonata", quindi il
 * timeout schermo scatta normalmente e su molti device (Huawei/EMUI in testa)
 * lo spegnimento sospende la cattura del microfono di Chrome — l'altro lato
 * smette di sentire. Tenere lo schermo acceso finché la call è viva elimina
 * il trigger alla radice.
 *
 * Il browser rilascia da solo il sentinel quando la pagina va in background,
 * quindi al ritorno in foreground va ri-acquisito (listener visibilitychange).
 * Se la Wake Lock API manca o la richiesta viene rifiutata (battery saver,
 * o contesto in-app-browser tipo SFSafariViewController di Messaggi.app, meno
 * affidabile della Safari piena) si logga l'evento e si passa a un fallback:
 * un video muto in loop, tecnica nota per tenere sveglio lo schermo dove la
 * Wake Lock API non è disponibile. In ogni caso la call funziona comunque.
 */

let wanted = false;
let sentinel: WakeLockSentinel | null = null;
let requesting = false;
let listenerAttached = false;
let fallbackVideo: HTMLVideoElement | null = null;
let currentContextId: string | undefined;

// 1x1, ~0.2s, webm nero muto — tecnica nota (NoSleep.js) per impedire lo
// spegnimento schermo riproducendo media in loop quando manca Wake Lock API.
const SILENT_VIDEO_SRC =
  'data:video/webm;base64,GkXfo59ChoEBQveBAULygQRC84EIQoKEd2VibUKHgQJChYECGFOAZwEAAAAAAAHTEU2bdLpNu4tTq4QVSalmU6yBoU27i1OrhBZUrmtTrIHfTbuMU6uEElTDZ1OsggEXTbuMU6uEHFO7a1OsggI+7AEAAAAAAABZAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=';

function ensureFallbackVideo(): HTMLVideoElement {
  if (!fallbackVideo) {
    const video = document.createElement('video');
    video.setAttribute('muted', '');
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');
    video.setAttribute('aria-hidden', 'true');
    video.style.position = 'fixed';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';
    video.src = SILENT_VIDEO_SRC;
    document.body.appendChild(video);
    fallbackVideo = video;
  }
  return fallbackVideo;
}

function startFallbackVideo(): void {
  const video = ensureFallbackVideo();
  // play() restituisce una Promise solo dai browser recenti: su WebView datate
  // — e in jsdom — torna undefined, e concatenarvi .catch() solleverebbe un
  // errore non gestito. Stessa guardia usata in ringtone.ts e callerTone.ts.
  const played = video.play();
  if (played && typeof played.catch === 'function') {
    void played.catch(() => {
      // Anche il fallback può essere rifiutato: la call prosegue comunque.
    });
  }
}

function stopFallbackVideo(): void {
  if (!fallbackVideo) return;
  fallbackVideo.pause();
  fallbackVideo.remove();
  fallbackVideo = null;
}

function reportWakeLockIssue(code: string, message: string): void {
  reportClientErrorOnce(`screen-wake-lock-${code}-${currentContextId ?? 'unknown'}`, {
    code,
    message,
    context: { callSessionId: currentContextId, visibility: document.visibilityState },
  });
}

async function acquire(): Promise<void> {
  if (!wanted || requesting) return;
  if (sentinel && !sentinel.released) return;
  // Scheda in secondo piano: il browser rifiuta sempre, non è un errore da
  // segnalare. Si riprova da onVisibilityChange quando torna visibile.
  if (document.visibilityState !== 'visible') return;
  const wakeLock = navigator.wakeLock;
  if (!wakeLock) {
    reportWakeLockIssue('UNSUPPORTED', 'Wake Lock API non disponibile nel browser');
    startFallbackVideo();
    return;
  }
  requesting = true;
  try {
    sentinel = await wakeLock.request('screen');
    stopFallbackVideo();
  } catch {
    // Rifiutato (battery saver, policy): fallback al video in loop.
    sentinel = null;
    reportWakeLockIssue('REJECTED', 'Richiesta Wake Lock rifiutata dal browser');
    startFallbackVideo();
  } finally {
    requesting = false;
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void acquire();
}

export function keepScreenAwake(callSessionId?: string): void {
  wanted = true;
  currentContextId = callSessionId;
  if (!listenerAttached) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    listenerAttached = true;
  }
  void acquire();
}

export function releaseScreenAwake(): void {
  wanted = false;
  currentContextId = undefined;
  if (listenerAttached) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listenerAttached = false;
  }
  const current = sentinel;
  sentinel = null;
  if (current && !current.released) void current.release().catch(() => {});
  stopFallbackVideo();
}

export function __resetWakeLockForTests(): void {
  releaseScreenAwake();
}
